import React, { useState } from "react";
import { motion } from "framer-motion";
import styled from "styled-components";
import tw from "twin.macro";
import { css } from "styled-components/macro"; //eslint-disable-line
import { SectionHeading, Subheading as SubheadingBase } from "components/misc/Headings.js";
import { SectionDescription } from "components/misc/Typography.js";
import { Container, ContentWithPaddingXl } from "components/misc/Layouts.js";
import { ReactComponent as ChevronDownIcon } from "feather-icons/dist/icons/chevron-down.svg";
import { ReactComponent as SvgDecoratorBlob1 } from "images/svg-decorator-blob-7.svg";
import { ReactComponent as SvgDecoratorBlob2 } from "images/svg-decorator-blob-8.svg";

const Subheading = tw(SubheadingBase)`mb-4 text-center`;
const Heading = tw(SectionHeading)`w-full`;
const Description = tw(SectionDescription)`w-full text-center`;

const Column = tw.div`flex flex-col items-center`;
const HeaderContent = tw.div``;

const FAQSContainer = tw.dl`mt-12 max-w-4xl relative`;
const FAQ = tw.div`cursor-pointer select-none mt-5 px-8 sm:px-10 py-5 sm:py-4 rounded-lg text-gray-800 hover:text-gray-900 bg-gray-200 hover:bg-gray-300 transition duration-300`;
const Question = tw.dt`flex justify-between items-center`;
const QuestionText = tw.span`text-lg lg:text-xl font-semibold`;
const QuestionToggleIcon = motion(styled.span`
  ${tw`ml-2 transition duration-300`}
  svg {
    ${tw`w-6 h-6`}
  }
`);
const Answer = motion(tw.dd`pointer-events-none text-sm sm:text-base leading-relaxed`);

const DecoratorBlob1 = styled(SvgDecoratorBlob1)`
  ${tw`pointer-events-none -z-20 absolute right-0 top-0 h-56 w-56 opacity-15 transform translate-x-2/3 -translate-y-12 text-teal-400`}
`;
const DecoratorBlob2 = styled(SvgDecoratorBlob2)`
  ${tw`pointer-events-none -z-20 absolute left-0 bottom-0 h-64 w-64 opacity-15 transform -translate-x-2/3 text-primary-500`}
`;



export default ({
  subheading = "FAQS",
  heading = "Vous avez des questions ?",
  description = "Et nous avons des réponses à chacune d'elles.",
  faqs = [
    {
      question: "Est-ce que Tanga est disponible en Côte d'Ivoire ?",
      answer:
        "Oui, Tanga est disponible en Côte d'Ivoire. Nous sommes présents dans plusieurs villes de la Côte d'Ivoire, notamment à Abidjan, Bouaké, Yamoussoukro, et plusieurs autres."
    },
    {
      question: "Qu'est-ce que Tanga ?",
      answer: "Tanga est une plateforme digitale de gestion des visiteurs qui améliore la sécurité, la traçabilité et l'expérience d'accueil dans les entreprises et organisations."
    },
    {
      question: "Comment fonctionne le check-in avec Tanga ?",
      answer: "Les visiteurs peuvent être pré-enregistrés et reçoivent un QR code ou utilisent leur numéro de téléphone pour un check-in rapide et sécurisé à l'entrée."
    },
    {
      question: "Quels sont les avantages pour les entreprises ?",
      answer: "Tanga permet de gagner du temps, d'améliorer la sécurité, de générer des rapports détaillés sur les flux visiteurs, de simplifier la gestion des accès et d'assurer la conformité RGPD."
    },
    {
      question: "Quels sont les avantages pour les visiteurs ?",
      answer: "Les visiteurs bénéficient d'un enregistrement unique, d'un check-in rapide, d'une expérience d'accueil moderne et sans stress, et d'une meilleure confidentialité de leurs données."
    },
    {
      question: "Tanga est-il conforme à la réglementation sur la protection des données (RGPD/ARTCI) ?",
      answer: "Oui, Tanga applique des mesures strictes de sécurité et de conformité RGPD et ARTCI pour protéger toutes les données personnelles des visiteurs et des entreprises."
    },
    {
      question: "Où Tanga est-il disponible ?",
      answer: "Tanga est disponible en Côte d'Ivoire et dans plusieurs autres pays d'Afrique. Contactez-nous pour savoir si votre ville ou votre entreprise peut bénéficier de Tanga."
    },
    {
      question: "Comment planifier ou gérer un rendez-vous visiteur ?",
      answer: "Les équipes de sécurité et d'accueil peuvent planifier, pré-enregistrer et gérer les rendez-vous directement sur la plateforme Tanga."
    },
    {
      question: "Comment contacter le support Tanga ?",
      answer: "Vous pouvez contacter notre équipe support à support@tangagroup.africa pour toute question ou assistance."
    }
  ]
}) => {
  const [activeQuestionIndex, setActiveQuestionIndex] = useState(null);

  const toggleQuestion = questionIndex => {
    if (activeQuestionIndex === questionIndex) setActiveQuestionIndex(null);
    else setActiveQuestionIndex(questionIndex);
  };

  return (
    <Container>
      <ContentWithPaddingXl>
        <Column>
          <HeaderContent>
            {subheading && <Subheading>{subheading}</Subheading>}
            <Heading>{heading}</Heading>
            {description && <Description>{description}</Description>}
          </HeaderContent>
          <FAQSContainer>
            {faqs.map((faq, index) => (
              <FAQ
                key={index}
                onClick={() => {
                  toggleQuestion(index);
                }}
                className="group"
              >
                <Question>
                  <QuestionText>{faq.question}</QuestionText>
                  <QuestionToggleIcon
                    variants={{
                      collapsed: { rotate: 0 },
                      open: { rotate: -180 }
                    }}
                    initial="collapsed"
                    animate={activeQuestionIndex === index ? "open" : "collapsed"}
                    transition={{ duration: 0.02, ease: [0.04, 0.62, 0.23, 0.98] }}
                  >
                    <ChevronDownIcon />
                  </QuestionToggleIcon>
                </Question>
                <Answer
                  variants={{
                    open: { opacity: 1, height: "auto", marginTop: "16px" },
                    collapsed: { opacity: 0, height: 0, marginTop: "0px" }
                  }}
                  initial="collapsed"
                  animate={activeQuestionIndex === index ? "open" : "collapsed"}
                  transition={{ duration: 0.3, ease: [0.04, 0.62, 0.23, 0.98] }}
                >
                  {faq.answer}
                </Answer>
              </FAQ>
            ))}
          </FAQSContainer>
        </Column>
      </ContentWithPaddingXl>
      <DecoratorBlob1/>
      <DecoratorBlob2 />
    </Container>
  );
};
